/**
 * POIGrid — renders structured tool results from the get_pois tool as
 * inline rows below an Ask Hauser assistant message. Top 3 shown by
 * default with a "See all" expander for the rest. Rows open Google
 * Maps via the place_id when clicked.
 *
 * Always vertical-stacked — the chat drawer is narrower than the
 * viewport, so a viewport-based responsive switch (isMobile) doesn't
 * help. Stacking always keeps the card content readable inside the
 * drawer regardless of screen size.
 *
 * Visual: matches the row-card pattern used by ConditionScore
 * subfactor items and MaintenanceReserveCard plan items
 * (#2a2a2a bg, 10px 14px padding, 10px radius, hover lift).
 *
 * The shape of `pois` matches what the backend get_pois handler returns
 * (see packages/functions/api/ask-hauser/context/toolHandlers.ts —
 * { type, name, distance?, rating? }).
 */
import { Box, keyframes } from '@mui/material';
import {
  AlertTriangle,
  Ambulance,
  BookOpen,
  Coffee,
  Cross,
  Dumbbell,
  ExternalLink,
  Flame,
  Fuel,
  GraduationCap,
  MapPin,
  Pill,
  ShoppingBag,
  ShoppingCart,
  Shield,
  Star,
  Store,
  Trees,
  TrainFront,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { useState } from 'react';

import type { ComponentType } from 'react';

import { COLORS } from '@/components/dashboard/scoring';

/**
 * Category id → lucide icon. Mirrors the keys under
 * `nearest_points_of_interest` returned by HauserEQ — the lifestyle
 * categories from Mark's google-places-data lambda + the PropertyLens
 * safety / medical / hazard categories that have always been there.
 *
 * `type` on each POI record carries the category key (set by the
 * `get_pois` tool handler in toolHandlers.ts). Unknown / missing types
 * fall back to MapPin.
 */
const CATEGORY_ICON: Record<string, ComponentType<{ size?: number; color?: string }>> = {
  // Lifestyle (Google Places)
  groceries: ShoppingCart,
  pharmacies: Pill,
  gas_stations: Fuel,
  cafes: Coffee,
  restaurants: UtensilsCrossed,
  parks: Trees,
  gyms: Dumbbell,
  libraries: BookOpen,
  shopping_malls: ShoppingBag,
  department_stores: Store,
  transit: TrainFront,
  senior_care: Users,
  // PropertyLens (existing HauserEQ POI categories)
  schools: GraduationCap,
  public_schools: GraduationCap,
  private_schools: GraduationCap,
  colleges_and_universities: GraduationCap,
  childcare_centers: GraduationCap,
  hospitals: Cross,
  hospitals_and_medical_centers: Cross,
  fire_stations: Flame,
  police_stations: Shield,
  law_enforcement_facilities: Shield,
  ambulance_services: Ambulance,
  superfund_sites: AlertTriangle,
  brownfields: AlertTriangle,
  toxic_release_facilities: AlertTriangle,
  underground_storage_tanks: AlertTriangle,
  asbestos_facilities: AlertTriangle,
};

const iconForType = (type?: string): ComponentType<{ size?: number; color?: string }> =>
  (type && CATEGORY_ICON[type]) || MapPin;

interface POIRecord {
  type?: string;
  name: string;
  /** Optional Google place_id — when present, the card links to Google Maps. */
  place_id?: string;
  distance?: { value: number; unit: string };
  rating?: number;
}

interface POIGridProps {
  pois: POIRecord[];
}

const INITIAL_VISIBLE = 3;

const googleMapsUrl = (poi: POIRecord): string => {
  if (poi.place_id) {
    // Maps deep link via place_id — most reliable, opens the place card
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      poi.name,
    )}&query_place_id=${encodeURIComponent(poi.place_id)}`;
  }
  // Fallback: search by name. Less precise but always works.
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(poi.name)}`;
};

const POIRow = ({ poi, showIcon }: { poi: POIRecord; showIcon: boolean }) => {
  const Icon = iconForType(poi.type);
  return (
    <Box
      component="a"
      href={googleMapsUrl(poi)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`poi-card-${poi.name.toLowerCase().replace(/\s+/g, '-')}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 14px',
        // Taupe tint matches the disclaimer pill above so cards feel like
        // one consistent "subtle data block" style within the chat.
        background: 'var(--tx-fill)',
        borderRadius: '8px',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'background 0.15s',
        '&:hover': { background: 'rgba(187,183,175,0.14)' },
      }}>
      {showIcon && (
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            background: 'var(--tx-fill)',
            color: COLORS.textDim,
          }}>
          <Icon size={14} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: COLORS.text,
          fontFamily: "'Manrope', sans-serif",
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {poi.name}
      </Box>
      {(poi.distance != null || poi.rating != null) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: 11,
            color: COLORS.textDim,
            fontFamily: "'Manrope', sans-serif",
          }}>
          {poi.distance != null && (
            <span>
              {poi.distance.value.toFixed(1)} {poi.distance.unit}
            </span>
          )}
          {poi.rating != null && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <Star
                size={11}
                fill="#C0FF02"
                stroke="none"
                style={{ opacity: 0.8 }}
              />
              <span>{poi.rating.toFixed(1)}</span>
            </Box>
          )}
        </Box>
      )}
    </Box>
      <ExternalLink size={12} style={{ flexShrink: 0, opacity: 0.4 }} />
    </Box>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const POIGrid = ({ pois }: POIGridProps) => {
  const [expanded, setExpanded] = useState(false);

  if (!pois || pois.length === 0) return null;

  const visible = expanded ? pois : pois.slice(0, INITIAL_VISIBLE);
  const remaining = pois.length - INITIAL_VISIBLE;
  // Hide per-row icons when every visible card is the same category — the
  // repetition adds noise. Show them when the response spans multiple
  // categories so users can distinguish a grocery from a cafe at a glance.
  const uniqueTypes = new Set(
    visible.map((p) => p.type).filter((t): t is string => typeof t === 'string'),
  );
  const showIcons = uniqueTypes.size > 1;

  return (
    <Box
      data-testid="poi-grid"
      sx={{
        mt: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        animation: `${fadeIn} 350ms ease-out both`,
      }}>
      {visible.map((poi, i) => (
        <POIRow key={poi.place_id ?? `${poi.name}-${i}`} poi={poi} showIcon={showIcons} />
      ))}
      {remaining > 0 && (
        <Box
          data-testid="poi-grid-toggle"
          onClick={() => setExpanded((e) => !e)}
          sx={{
            fontSize: 11,
            color: COLORS.textDim,
            cursor: 'pointer',
            alignSelf: 'flex-start',
            padding: '4px 2px',
            fontFamily: "'Manrope', sans-serif",
            '&:hover': { color: COLORS.text },
          }}>
          {expanded ? 'Show less' : `See all (${pois.length})`}
        </Box>
      )}
    </Box>
  );
};

export default POIGrid;
